import { Typography } from "@mui/material";
import { EmployeeStatusCell } from "Components/Merchant/Employees/EmployeesTable/EmployeeStatusCell";
import { useResponsiveValue } from "Hooks/useResponsiveValue";
import {
  EmployeeStatus,
  type EmployeeDto,
  type PhoneNumber,
} from "Infrastructure/Api/Api";
import { formatPhone } from "Utils/PhoneUtils";
import { appUrl } from "Utils/UrlUtils";
import { useTableRowOnClickHandler } from "Hooks/useTableRowOnClickHandler";
import {
  type MRT_ColumnDef,
  type MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { Resources, useResource } from "Translations/Resources";
import { useTableLocalization } from "Utils/TableLocalizationUtils";

const RESOURCES = Resources.Employee.List.FilterLabels;

type Props = {
  isLoading?: boolean;
  data: Array<EmployeeDto>;
  rowCount: number;
  tableProps: Partial<MRT_TableOptions<EmployeeDto>>;
};

export const EmployeesTable = ({
  isLoading,
  data,
  rowCount,
  tableProps,
}: Props) => {
  const getResponsiveValue = useResponsiveValue();
  const localization = useTableLocalization();
  const createRowClickHandler = useTableRowOnClickHandler();
  const { t } = useResource();

  const columns = useMemo<Array<MRT_ColumnDef<EmployeeDto>>>(
    () => [
      {
        accessorKey: "status",
        header: t(Resources.Common.Status),
        size: 1,
        Cell: ({ row }) => <EmployeeStatusCell status={row.original.status} />,
        filterVariant: "multi-select",
        filterSelectOptions: [
          {
            label: t(RESOURCES.Created),
            value: EmployeeStatus.Created,
          },
          {
            label: t(RESOURCES.InvitedWaitingForConfirmation),
            value: EmployeeStatus.InvitedWaitingForConfirmation,
          },
          {
            label: t(RESOURCES.Active),
            value: EmployeeStatus.Active,
          },
          {
            label: t(RESOURCES.Inactive),
            value: EmployeeStatus.Inactive,
          },
        ],
        enableSorting: false,
      },
      {
        accessorKey: "firstName",
        header: t(Resources.Common.FirstName),
        size: getResponsiveValue([150, 200]),
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: "lastName",
        header: t(Resources.Common.LastName),
        size: getResponsiveValue([150, 200]),
        Cell: ({ cell }) => (
          <Typography fontSize={12} fontWeight={400} noWrap>
            {cell.getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: "phone",
        header: t(Resources.Common.Phone),
        size: getResponsiveValue([120, 140]),
        Cell: ({ cell }) => formatPhone(cell.getValue<PhoneNumber>()),
        enableSorting: false,
      },
      {
        accessorKey: "email",
        header: t(Resources.Common.Email),
        size: 250,
      },
    ],
    [getResponsiveValue, t],
  );

  const table = useMaterialReactTable({
    ...tableProps,
    data,
    columns,
    rowCount,
    localization,
    enableColumnResizing: true,
    columnResizeMode: "onChange",
    state: {
      ...tableProps.state,
      isLoading,
    },
    initialState: {
      ...tableProps.initialState,
      density: "compact",
    },
    muiTableBodyRowProps: ({ row }) => {
      const url = `${appUrl("employee-detail")}/${row.original.publicID}`;
      const onClick = createRowClickHandler(url);
      return {
        sx: { cursor: "pointer" },
        onClick,
      };
    },
    muiTablePaperProps: { sx: { borderRadius: "10px" } },
  });

  return <MaterialReactTable table={table} />;
};
